import { ArchiveNotice } from "@/components/chat/archive-notice";
import { ChatInput } from "@/components/chat/chat-input";
import { MessageFeedback } from "@/components/chat/message-feedback";
import { MessageSources } from "@/components/chat/message-sources";
import { ShareChatDialog } from "@/components/chat/share-chat-dialog";
import { AppSidebar } from "@/components/layout/app-sidebar";
import { CreateProjectDialog } from "@/components/projects/create-project-dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Markdown } from "@/components/ui/markdown";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger
} from "@/components/ui/sidebar";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiException } from "@/lib/api";
import { useAuthStore } from "@/stores/auth.store";
import { useChatsStore } from "@/stores/chats.store";
import { useMessagesStore } from "@/stores/messages.store";
import { useModelsStore } from "@/stores/models.store";
import { useProjectsStore } from "@/stores/projects.store";
import {
  ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  MAX_FILES_PER_MESSAGE,
  MAX_TOTAL_FILES_SIZE,
  type CreateMessageResponseDto,
  type ReadChatDto,
  type ReadChatWithFilesDto,
  type ReadMessageWithSourcesDto,
  type ReadProjectDto,
  type ReadProjectFileDto,
  type ReadProjectWithRelationsDto
} from "@package/types";
import {
  createRoute,
  Outlet,
  redirect,
  useNavigate,
  useParams
} from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  Bot,
  Clock,
  Download,
  FileText,
  Folder,
  Loader2,
  MessageSquare,
  Paperclip,
  Plus,
  Save,
  Search,
  Trash2,
  Upload,
  User
} from "lucide-react";
import {
  type ChangeEvent,
  type MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { rootRoute } from "./__root";
import { cn } from "@/lib/utils";

const requireAuth = () => {
  const { isAuthenticated } = useAuthStore.getState();

  if (!isAuthenticated) {
    throw redirect({ to: "/login" });
  }
};

function addResponseMessages(
  response: CreateMessageResponseDto,
  addMessage: (message: ReadMessageWithSourcesDto) => void
) {
  addMessage(response.message);
}

// A failed send must not leave its optimistic message behind. Refetching on top of the
// removal lets the server decide what the chat really holds - it may be running another
// tab's message (409) or still be working on one whose request we lost.
function discardTempMessage(chatId: string | null, messageId: number | null) {
  if (!chatId || messageId === null) return;

  const { removeMessage, loadMessages } = useMessagesStore.getState();
  removeMessage(chatId, messageId);
  void loadMessages(chatId, true);
}

// Layout Route
export const chatsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/chats",
  beforeLoad: requireAuth,
  component: WorkspaceLayout
});

export const projectsLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/projects",
  beforeLoad: requireAuth,
  component: WorkspaceLayout
});

// Index Route (no chat selected)
export const chatsIndexRoute = createRoute({
  getParentRoute: () => chatsLayoutRoute,
  path: "/",
  component: ChatsIndexPage
});

// Detail Route (chat selected)
export const chatDetailRoute = createRoute({
  getParentRoute: () => chatsLayoutRoute,
  path: "/$chatId",
  component: ChatDetailPage
});

export const legacyProjectDetailRoute = createRoute({
  getParentRoute: () => chatsLayoutRoute,
  path: "/projects/$projectId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/projects/$projectId",
      params: { projectId: params.projectId }
    });
  }
});

export const projectsIndexRoute = createRoute({
  getParentRoute: () => projectsLayoutRoute,
  path: "/",
  component: ProjectsIndexPage
});

export const projectDetailRoute = createRoute({
  getParentRoute: () => projectsLayoutRoute,
  path: "/$projectId",
  component: ProjectDetailPage
});

// Layout Component
function WorkspaceLayout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loadModels = useModelsStore((s) => s.loadModels);
  const loadChats = useChatsStore((s) => s.loadChats);
  const removeChat = useChatsStore((s) => s.removeChat);
  const loadProjects = useProjectsStore((s) => s.loadProjects);
  const removeChatFromProjects = useProjectsStore(
    (s) => s.removeChatFromProjects
  );
  const [chatToDelete, setChatToDelete] = useState<string | null>(null);
  const [chatToShare, setChatToShare] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const params = useParams({ strict: false });
  const currentChatId = (params as { chatId?: string }).chatId;

  useEffect(() => {
    loadChats();
    loadProjects();
    loadModels();
  }, [loadChats, loadModels, loadProjects]);

  const deleteChat = async (e: MouseEvent) => {
    e.preventDefault();
    if (!chatToDelete) return;

    try {
      setIsDeleting(true);
      await api.delete(`/chats/${chatToDelete}`);
      removeChat(chatToDelete);
      removeChatFromProjects(chatToDelete);
      toast.success(t("chats.deleted"));

      if (currentChatId === chatToDelete) {
        navigate({ to: "/chats" });
      }
    } catch (error) {
      console.error("Failed to delete chat:", error);
      toast.error(t("chats.deleteError"));
    } finally {
      setIsDeleting(false);
      setChatToDelete(null);
    }
  };

  return (
    <SidebarProvider className="h-svh min-h-0 overflow-hidden">
      <AppSidebar onDeleteChat={setChatToDelete} onShareChat={setChatToShare} />
      <SidebarInset className="h-svh min-h-0">
        <Outlet />
      </SidebarInset>

      <ShareChatDialog
        chatId={chatToShare}
        onOpenChange={(open) => !open && setChatToShare(null)}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={!!chatToDelete}
        onOpenChange={(open) => !open && setChatToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("chats.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("chats.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteChat}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              {t("chats.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}

function getGreeting(t: (key: string) => string, name: string): string {
  const hour = new Date().getHours();

  if (hour >= 5 && hour < 11) {
    return t("welcome.morning").replace("{name}", name);
  } else if (hour >= 11 && hour < 18) {
    return t("welcome.afternoon").replace("{name}", name);
  } else if (hour >= 18 && hour < 22) {
    return t("welcome.evening").replace("{name}", name);
  } else {
    return t("welcome.night").replace("{name}", name);
  }
}

// Index Page Component - Welcome Screen
function ChatsIndexPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const addChat = useChatsStore((s) => s.addChat);
  const setSending = useChatsStore((s) => s.setSending);
  const updateProjectChat = useProjectsStore((s) => s.updateProjectChat);
  const addMessage = useMessagesStore((s) => s.addMessage);
  const setCurrentChatId = useMessagesStore((s) => s.setCurrentChatId);
  const [isCreatingChat, setIsCreatingChat] = useState(false);

  const handleSendFromWelcome = async (
    content: string,
    files: File[],
    modelId: string
  ) => {
    if (!content.trim() && files.length === 0) return;

    setIsCreatingChat(true);
    let chatId: string | null = null;
    let tempMessageId: number | null = null;

    try {
      const newChat = await api.post<ReadChatDto>("/chats");
      chatId = newChat.id;
      addChat(newChat);
      updateProjectChat(newChat);

      setSending(chatId, true);

      const id = chatId;

      const tempFiles = files.map((file, index) => ({
        id: -(index + 1),
        chatId: id,
        messageId: 0,
        openaiFileId: "",
        filename: file.name,
        mimeType: file.type,
        createdAt: new Date(),
        updatedAt: new Date()
      }));

      const tempUserMessage: ReadMessageWithSourcesDto = {
        id: Date.now(),
        chatId: id,
        modelId: null,
        promptId: null,
        type: "USER",
        content,
        totalTokens: null,
        sources: [],
        files: tempFiles,
        prompt: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // Set currentChatId BEFORE adding the message and navigating
      // This prevents loadMessages() from clearing the temp message
      setCurrentChatId(id);
      addMessage(tempUserMessage);
      tempMessageId = tempUserMessage.id;

      navigate({ to: "/chats/$chatId", params: { chatId } });

      let response: CreateMessageResponseDto;

      if (files.length > 0) {
        const formData = new FormData();
        formData.append("modelId", modelId);
        formData.append("content", content);
        files.forEach((file) => formData.append("files", file));

        response = await api.postFormData<CreateMessageResponseDto>(
          `/chats/${chatId}/messages`,
          formData
        );
      } else {
        response = await api.post<CreateMessageResponseDto>(
          `/chats/${chatId}/messages`,
          {
            modelId: Number(modelId),
            content
          }
        );
      }

      addResponseMessages(response, addMessage);

      if (files.length > 0) {
        api
          .get<ReadChatWithFilesDto>(`/chats/${id}`)
          .then((chat) =>
            useChatsStore.getState().setFiles(id, chat.files || [])
          )
          .catch(() => {});
      }

      if (response.chat) {
        const { updateChat } = useChatsStore.getState();
        updateChat(response.chat);
        updateProjectChat(response.chat);
      }
    } catch (error) {
      discardTempMessage(chatId, tempMessageId);

      if (error instanceof ApiException && error.status === 409) {
        toast.error(t("chat.errorAlreadyProcessing"));
      } else if (error instanceof ApiException) {
        toast.error(t("chat.errorSending"), {
          description: error.message
        });
      } else {
        toast.error(t("chat.errorSending"));
      }
    } finally {
      setIsCreatingChat(false);
      if (chatId) {
        setSending(chatId, false);
      }
    }
  };

  const canWrite = user?.canWrite ?? false;
  const greeting = getGreeting(t, user?.name || "");

  return (
    <div className="flex h-full min-h-0 flex-col overflow-y-auto">
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <span className="font-medium">{t("common.appName")}</span>
        </div>
      </header>

      <div className="flex min-h-fit flex-1 flex-col items-center justify-center px-8 py-6 sm:p-8">
        <div className="mb-6 flex flex-col items-center text-center sm:mb-8">
          <h1 className="mb-2 text-3xl font-semibold tracking-tight sm:text-4xl">
            {greeting}
          </h1>
          {canWrite && (
            <p className="text-base text-muted-foreground sm:text-lg">
              {t("welcome.subtitle")}
            </p>
          )}
        </div>

        <div className="w-full max-w-2xl">
          {canWrite ? (
            <ChatInput
              onSend={handleSendFromWelcome}
              isSending={isCreatingChat}
              centered
            />
          ) : (
            <ArchiveNotice />
          )}
        </div>
      </div>
    </div>
  );
}

function getProjectActivityDate(project: ReadProjectWithRelationsDto): Date {
  const timestamps = [
    project.updatedAt,
    ...project.chats.map((chat) => chat.updatedAt),
    ...project.files.map((file) => file.updatedAt)
  ]
    .map((value) => new Date(value).getTime())
    .filter((value) => Number.isFinite(value));

  return new Date(Math.max(...timestamps));
}

function formatRelativeDate(date: Date, locale: string): string {
  const secondsDifference = Math.round((date.getTime() - Date.now()) / 1000);
  const absoluteSeconds = Math.abs(secondsDifference);
  const units: Array<{
    unit: Intl.RelativeTimeFormatUnit;
    seconds: number;
  }> = [
    { unit: "year", seconds: 31_536_000 },
    { unit: "month", seconds: 2_592_000 },
    { unit: "week", seconds: 604_800 },
    { unit: "day", seconds: 86_400 },
    { unit: "hour", seconds: 3_600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 }
  ];

  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const matchingUnit =
    units.find((candidate) => absoluteSeconds >= candidate.seconds) ??
    ({ unit: "second", seconds: 1 } satisfies {
      unit: Intl.RelativeTimeFormatUnit;
      seconds: number;
    });

  return formatter.format(
    Math.round(secondsDifference / matchingUnit.seconds),
    matchingUnit.unit
  );
}

function ProjectsIndexPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const projects = useProjectsStore((s) => s.projects);
  const isLoadingProjects = useProjectsStore((s) => s.isLoading);
  const removeProject = useProjectsStore((s) => s.removeProject);
  const removeProjectFromChats = useChatsStore((s) => s.removeProjectFromChats);
  const canWrite = useAuthStore((s) => s.user?.canWrite ?? false);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"activity" | "name">("activity");
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] =
    useState<ReadProjectWithRelationsDto | null>(null);
  const [isDeletingProject, setIsDeletingProject] = useState(false);

  const filteredProjects = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return projects
      .filter((project) =>
        normalizedQuery
          ? [project.name, project.description ?? ""]
              .join(" ")
              .toLowerCase()
              .includes(normalizedQuery)
          : true
      )
      .sort((left, right) => {
        if (sortBy === "name") {
          return left.name.localeCompare(right.name, i18n.language);
        }

        return (
          getProjectActivityDate(right).getTime() -
          getProjectActivityDate(left).getTime()
        );
      });
  }, [i18n.language, projects, searchQuery, sortBy]);

  const deleteProject = async (event: MouseEvent) => {
    event.preventDefault();
    if (!projectToDelete) return;

    try {
      setIsDeletingProject(true);
      await api.delete(`/projects/${projectToDelete.id}`);
      removeProject(projectToDelete.id);
      removeProjectFromChats(projectToDelete.id);
      toast.success(t("projects.deleted"));
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error(t("projects.deleteError"));
    } finally {
      setIsDeletingProject(false);
      setProjectToDelete(null);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea className="flex-1">
        <main className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-4 py-8 lg:px-8 lg:py-12">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <SidebarTrigger className="md:hidden" />
              <h1 className="text-3xl font-semibold tracking-normal">
                {t("projects.title")}
              </h1>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="hidden text-sm text-muted-foreground sm:inline">
                  {t("projects.sortLabel")}
                </span>
                <Select
                  value={sortBy}
                  onValueChange={(value) =>
                    setSortBy(value as "activity" | "name")
                  }
                >
                  <SelectTrigger className="h-11 w-36 md:h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="activity">
                      {t("projects.sortActivity")}
                    </SelectItem>
                    <SelectItem value="name">
                      {t("projects.sortName")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {canWrite && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="size-4" />
                  {t("projects.newProject")}
                </Button>
              )}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={t("projects.searchPlaceholder")}
              className="h-12 rounded-lg pl-9 text-base"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="rounded-full bg-accent px-4 py-2 text-sm font-medium text-accent-foreground">
              {t("projects.yourProjects")}
            </span>
          </div>

          {isLoadingProjects ? (
            <div className="grid gap-4 md:grid-cols-2">
              {Array.from({ length: 4 }).map((_, index) => (
                <div
                  key={index}
                  className="h-32 animate-pulse rounded-lg border bg-muted/40"
                />
              ))}
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
              {searchQuery.trim()
                ? t("projects.searchEmpty")
                : t("projects.noProjects")}
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {filteredProjects.map((project) => {
                const activityDate = getProjectActivityDate(project);

                return (
                  <div
                    key={project.id}
                    className="group relative flex min-h-32 rounded-lg border bg-card text-card-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        navigate({
                          to: "/projects/$projectId",
                          params: { projectId: project.id }
                        })
                      }
                      className="flex min-w-0 flex-1 flex-col justify-between p-5 pr-14 text-left"
                    >
                      <div className="space-y-3">
                        <h2 className="truncate text-base font-semibold">
                          {project.name}
                        </h2>
                        {project.description ? (
                          <p className="line-clamp-2 text-sm text-muted-foreground">
                            {project.description}
                          </p>
                        ) : null}
                      </div>
                      <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="size-4" />
                        {t("projects.updatedLabel")}{" "}
                        {formatRelativeDate(activityDate, i18n.language)}
                      </p>
                    </button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute right-3 top-3 size-8 text-muted-foreground hover:text-destructive"
                      onClick={() => setProjectToDelete(project)}
                      disabled={
                        isDeletingProject && projectToDelete?.id === project.id
                      }
                      title={t("projects.delete")}
                    >
                      {isDeletingProject &&
                      projectToDelete?.id === project.id ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Trash2 className="size-4" />
                      )}
                      <span className="sr-only">{t("projects.delete")}</span>
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </main>
      </ScrollArea>

      <CreateProjectDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
      />

      <AlertDialog
        open={!!projectToDelete}
        onOpenChange={(open) => !open && setProjectToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("projects.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingProject}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteProject}
              disabled={isDeletingProject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingProject ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              {t("projects.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ProjectDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { projectId } = useParams({ strict: false }) as { projectId: string };
  const projects = useProjectsStore((s) => s.projects);
  const addProject = useProjectsStore((s) => s.addProject);
  const updateProject = useProjectsStore((s) => s.updateProject);
  const updateProjectInfo = useProjectsStore((s) => s.updateProjectInfo);
  const removeProject = useProjectsStore((s) => s.removeProject);
  const addProjectFiles = useProjectsStore((s) => s.addProjectFiles);
  const removeProjectFile = useProjectsStore((s) => s.removeProjectFile);
  const updateProjectChat = useProjectsStore((s) => s.updateProjectChat);
  const addChat = useChatsStore((s) => s.addChat);
  const updateChat = useChatsStore((s) => s.updateChat);
  const removeProjectFromChats = useChatsStore((s) => s.removeProjectFromChats);
  const setSending = useChatsStore((s) => s.setSending);
  const addMessage = useMessagesStore((s) => s.addMessage);
  const setCurrentChatId = useMessagesStore((s) => s.setCurrentChatId);
  const canWrite = useAuthStore((s) => s.user?.canWrite ?? false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoadingProject, setIsLoadingProject] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeletingProject, setIsDeletingProject] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingProjectFileId, setDeletingProjectFileId] = useState<
    number | null
  >(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const project = projects.find((item) => item.id === projectId);

  useEffect(() => {
    if (project) {
      setName(project.name);
      setDescription(project.description ?? "");
    }
  }, [project]);

  useEffect(() => {
    if (project || isLoadingProject) return;

    setIsLoadingProject(true);
    api
      .get<ReadProjectWithRelationsDto>(`/projects/${projectId}`)
      .then((loadedProject) => {
        const exists = useProjectsStore
          .getState()
          .projects.some((item) => item.id === loadedProject.id);
        if (exists) {
          updateProject(loadedProject);
        } else {
          addProject(loadedProject);
        }
      })
      .catch(() => toast.error(t("projects.loadError")))
      .finally(() => setIsLoadingProject(false));
  }, [addProject, isLoadingProject, project, projectId, t, updateProject]);

  const saveProject = async () => {
    if (!project || !name.trim()) return;

    try {
      setIsSaving(true);
      const updated = await api.patch<ReadProjectDto>(
        `/projects/${projectId}`,
        {
          name: name.trim(),
          description: description.trim() || null
        }
      );
      updateProjectInfo(updated);
      toast.success(t("projects.updated"));
    } catch (error) {
      console.error("Failed to update project:", error);
      toast.error(t("projects.updateError"));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProject = async (event: MouseEvent) => {
    event.preventDefault();
    if (!project) return;

    try {
      setIsDeletingProject(true);
      await api.delete(`/projects/${project.id}`);
      removeProject(project.id);
      removeProjectFromChats(project.id);
      toast.success(t("projects.deleted"));
      navigate({ to: "/projects" });
    } catch (error) {
      console.error("Failed to delete project:", error);
      toast.error(t("projects.deleteError"));
    } finally {
      setIsDeletingProject(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const createProjectChat = async () => {
    if (!project) return;

    try {
      setIsCreatingChat(true);
      const newChat = await api.post<ReadChatDto>(
        `/projects/${project.id}/chats`
      );
      addChat(newChat);
      updateProjectChat(newChat);
      navigate({ to: "/chats/$chatId", params: { chatId: newChat.id } });
    } catch (error) {
      console.error("Failed to create project chat:", error);
      toast.error(t("chats.createError"));
    } finally {
      setIsCreatingChat(false);
    }
  };

  const handleSendFromProject = async (
    content: string,
    files: File[],
    modelId: string
  ) => {
    if (!project || (!content.trim() && files.length === 0)) return;

    setIsCreatingChat(true);
    let chatId: string | null = null;
    let tempMessageId: number | null = null;

    try {
      const newChat = await api.post<ReadChatDto>(
        `/projects/${project.id}/chats`
      );
      const id = newChat.id;
      chatId = id;
      addChat(newChat);
      updateProjectChat(newChat);
      setSending(id, true);

      const tempFiles = files.map((file, index) => ({
        id: -(index + 1),
        chatId: id,
        messageId: 0,
        openaiFileId: "",
        filename: file.name,
        mimeType: file.type,
        createdAt: new Date(),
        updatedAt: new Date()
      }));

      const tempUserMessage: ReadMessageWithSourcesDto = {
        id: Date.now(),
        chatId: id,
        modelId: null,
        promptId: null,
        type: "USER",
        content,
        totalTokens: null,
        sources: [],
        files: tempFiles,
        prompt: null,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      setCurrentChatId(id);
      addMessage(tempUserMessage);
      tempMessageId = tempUserMessage.id;
      navigate({ to: "/chats/$chatId", params: { chatId: id } });

      let response: CreateMessageResponseDto;

      if (files.length > 0) {
        const formData = new FormData();
        formData.append("modelId", modelId);
        formData.append("content", content);
        files.forEach((file) => formData.append("files", file));

        response = await api.postFormData<CreateMessageResponseDto>(
          `/chats/${id}/messages`,
          formData
        );
      } else {
        response = await api.post<CreateMessageResponseDto>(
          `/chats/${id}/messages`,
          {
            modelId: Number(modelId),
            content
          }
        );
      }

      addResponseMessages(response, addMessage);

      if (files.length > 0 && chatId) {
        api
          .get<ReadChatWithFilesDto>(`/chats/${chatId}`)
          .then((chat) =>
            useChatsStore.getState().setFiles(chatId!, chat.files || [])
          )
          .catch(() => {});
      }

      if (response.chat) {
        updateChat(response.chat);
        updateProjectChat(response.chat);
      }
    } catch (error) {
      discardTempMessage(chatId, tempMessageId);

      if (error instanceof ApiException && error.status === 409) {
        toast.error(t("chat.errorAlreadyProcessing"));
      } else if (error instanceof ApiException) {
        toast.error(t("chat.errorSending"), {
          description: error.message
        });
      } else {
        toast.error(t("chat.errorSending"));
      }
    } finally {
      setIsCreatingChat(false);
      if (chatId) {
        setSending(chatId, false);
      }
    }
  };

  const handleFileSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const validFiles = files.filter((file) => {
      if (
        !ALLOWED_FILE_TYPES.includes(
          file.type as (typeof ALLOWED_FILE_TYPES)[number]
        )
      ) {
        toast.error(`${file.name}: ${t("chat.invalidFileType")}`);
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: ${t("chat.fileTooLarge")}`);
        return false;
      }
      return true;
    });

    if (selectedFiles.length + validFiles.length > MAX_FILES_PER_MESSAGE) {
      toast.error(t("chat.maxFilesReached"));
      return;
    }

    const totalSize = [...selectedFiles, ...validFiles].reduce(
      (sum, file) => sum + file.size,
      0
    );
    if (totalSize > MAX_TOTAL_FILES_SIZE) {
      toast.error(t("chat.totalFileSizeTooLarge"));
      return;
    }

    setSelectedFiles((currentFiles) => [...currentFiles, ...validFiles]);
    event.target.value = "";
  };

  const uploadProjectFiles = async () => {
    if (!project || selectedFiles.length === 0) return;

    try {
      setIsUploading(true);
      const formData = new FormData();
      selectedFiles.forEach((file) => formData.append("files", file));

      const uploadedFiles = await api.postFormData<ReadProjectFileDto[]>(
        `/projects/${project.id}/files`,
        formData
      );
      addProjectFiles(project.id, uploadedFiles);
      setSelectedFiles([]);
      toast.success(t("projects.filesUploaded"));
    } catch (error) {
      console.error("Failed to upload project files:", error);
      toast.error(t("projects.fileUploadError"));
    } finally {
      setIsUploading(false);
    }
  };

  const deleteProjectFile = async (file: ReadProjectFileDto) => {
    if (!project || deletingProjectFileId) return;

    try {
      setDeletingProjectFileId(file.id);
      const deletedFile = await api.delete<ReadProjectFileDto>(
        `/projects/${project.id}/files/${file.id}`
      );
      removeProjectFile(project.id, deletedFile.id);
      toast.success(t("projects.fileDeleted"));
    } catch (error) {
      console.error("Failed to delete project file:", error);
      toast.error(t("projects.fileDeleteError"));
    } finally {
      setDeletingProjectFileId(null);
    }
  };

  if (isLoadingProject && !project) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title={t("projects.title")} />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex h-full flex-col">
        <PageHeader title={t("projects.title")} />
        <div className="flex flex-1 items-center justify-center text-muted-foreground">
          {t("projects.notFound")}
        </div>
      </div>
    );
  }

  const activityDate = getProjectActivityDate(project);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <ScrollArea className="flex-1">
        <main className="mx-auto grid w-full max-w-7xl gap-8 px-4 py-8 lg:grid-cols-[minmax(0,1fr)_360px] lg:px-8 lg:py-12">
          <section className="min-w-0">
            <div className="mb-8 flex items-center gap-2">
              <SidebarTrigger className="md:hidden" />
              <Button
                variant="ghost"
                onClick={() => navigate({ to: "/projects" })}
                className="-ml-2 text-muted-foreground"
              >
                <ArrowLeft className="size-4" />
                {t("projects.allProjects")}
              </Button>
            </div>

            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 space-y-2">
                {canWrite ? (
                  <Input
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    onBlur={saveProject}
                    disabled={isSaving}
                    aria-label={t("projects.name")}
                    className="h-auto rounded-none border-0 bg-transparent px-0 text-3xl font-semibold shadow-none dark:bg-transparent focus-visible:ring-0"
                  />
                ) : (
                  <h1 className="truncate text-3xl font-semibold">
                    {project.name}
                  </h1>
                )}
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="size-4" />
                  {t("projects.updatedLabel")}{" "}
                  {formatRelativeDate(activityDate, i18n.language)}
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsDeleteDialogOpen(true)}
                  disabled={isDeletingProject}
                  title={t("projects.delete")}
                  className="text-muted-foreground hover:text-destructive"
                >
                  {isDeletingProject ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Trash2 className="size-4" />
                  )}
                  <span className="sr-only">{t("projects.delete")}</span>
                </Button>
                {canWrite && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={createProjectChat}
                    disabled={isCreatingChat}
                    title={t("projects.newChat")}
                  >
                    {isCreatingChat ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <Plus className="size-4" />
                    )}
                    <span className="sr-only">{t("projects.newChat")}</span>
                  </Button>
                )}
              </div>
            </div>

            <div className="mt-8 w-full">
              {canWrite ? (
                <ChatInput
                  onSend={handleSendFromProject}
                  isSending={isCreatingChat}
                  placeholder={t("projects.chatPlaceholder")}
                  fullWidth
                />
              ) : (
                <ArchiveNotice />
              )}
            </div>

            <section className="mt-8 space-y-2 border-t pt-3">
              {project.chats.length === 0 ? (
                <div className="py-5 text-sm text-muted-foreground">
                  {t("projects.noChats")}
                </div>
              ) : (
                project.chats.map((chat) => (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() =>
                      navigate({
                        to: "/chats/$chatId",
                        params: { chatId: chat.id }
                      })
                    }
                    className="group flex w-full cursor-pointer items-center justify-between gap-4 rounded-lg px-4 py-4 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {chat.title || t("chats.noTitle")}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground transition-colors group-hover:text-accent-foreground/70">
                        {t("projects.lastMessage")}{" "}
                        {formatRelativeDate(
                          new Date(chat.updatedAt),
                          i18n.language
                        )}
                      </span>
                    </span>
                    <MessageSquare className="size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-accent-foreground" />
                  </button>
                ))
              )}
            </section>
          </section>

          <aside className="space-y-4 lg:sticky lg:top-8 lg:self-start">
            <section className="rounded-lg border bg-card text-card-foreground">
              {canWrite ? (
                <div className="border-b p-5">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h2 className="text-base font-semibold">
                        {t("projects.notes")}
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        {t("projects.notesDescription")}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={saveProject}
                      disabled={isSaving || !name.trim()}
                      title={t("header.save")}
                    >
                      {isSaving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Save className="size-4" />
                      )}
                      <span className="sr-only">{t("header.save")}</span>
                    </Button>
                  </div>
                  <Textarea
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                    placeholder={t("projects.descriptionPlaceholder")}
                    disabled={isSaving}
                    className="min-h-24 resize-none"
                  />
                </div>
              ) : (
                project.description && (
                  <div className="border-b p-5">
                    <h2 className="mb-3 text-base font-semibold">
                      {t("projects.notes")}
                    </h2>
                    <p className="whitespace-pre-wrap text-sm">
                      {project.description}
                    </p>
                  </div>
                )
              )}

              <div className="p-5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALLOWED_FILE_TYPES.join(",")}
                  multiple
                  className="hidden"
                  onChange={handleFileSelect}
                />

                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold">
                      {t("projects.files")}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      {t("projects.filesDescription")}
                    </p>
                  </div>
                  {canWrite && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      title={t("projects.selectFiles")}
                    >
                      <Plus className="size-4" />
                      <span className="sr-only">
                        {t("projects.selectFiles")}
                      </span>
                    </Button>
                  )}
                </div>

                {selectedFiles.length > 0 && (
                  <div className="mb-3 rounded-lg border border-dashed p-3">
                    <p className="mb-2 text-xs font-medium text-muted-foreground">
                      {t("projects.pendingFiles")}
                    </p>
                    <div className="space-y-2">
                      {selectedFiles.map((file, index) => (
                        <div
                          key={`${file.name}-${index}`}
                          className="flex items-center gap-2 text-xs"
                        >
                          <FileText className="size-3 shrink-0 text-muted-foreground" />
                          <span className="min-w-0 flex-1 truncate">
                            {file.name}
                          </span>
                        </div>
                      ))}
                    </div>
                    <Button
                      className="mt-3 w-full"
                      size="sm"
                      onClick={uploadProjectFiles}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Upload className="size-4" />
                      )}
                      {t("projects.uploadFiles")}
                    </Button>
                  </div>
                )}

                {project.files.length === 0 ? (
                  <div className="rounded-lg bg-muted/40 p-5 text-center text-sm text-muted-foreground">
                    <Folder className="mx-auto mb-3 size-8 opacity-60" />
                    {t("projects.noFiles")}
                  </div>
                ) : (
                  <div className="divide-y rounded-lg border">
                    {project.files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-3 p-3"
                      >
                        <FileText className="size-4 shrink-0 text-muted-foreground" />
                        <div className="min-w-0 flex-1">
                          <span className="block truncate text-sm">
                            {file.filename}
                          </span>
                          {file.localVectorStatus === "failed" && (
                            <Badge
                              variant="outline"
                              className="mt-1 border-destructive/30 bg-destructive/10 text-destructive"
                            >
                              <AlertTriangle className="size-3" />
                              {t("projects.fileNotLocallySearchable")}
                            </Badge>
                          )}
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={deletingProjectFileId !== null}
                              title={t("projects.deleteFile")}
                              className="size-8 shrink-0 text-muted-foreground hover:text-destructive"
                            >
                              {deletingProjectFileId === file.id ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <Trash2 className="size-4" />
                              )}
                              <span className="sr-only">
                                {t("projects.deleteFile")}
                              </span>
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>
                                {t("projects.deleteFileTitle")}
                              </AlertDialogTitle>
                              <AlertDialogDescription>
                                {t("projects.deleteFileDescription")}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel
                                disabled={deletingProjectFileId === file.id}
                              >
                                {t("header.cancel")}
                              </AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteProjectFile(file)}
                                disabled={deletingProjectFileId === file.id}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                {deletingProjectFileId === file.id ? (
                                  <Loader2 className="size-4 animate-spin mr-2" />
                                ) : null}
                                {t("projects.deleteFileConfirm")}
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          </aside>
        </main>
      </ScrollArea>

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("projects.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("projects.deleteDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingProject}>
              {t("header.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteProject}
              disabled={isDeletingProject}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeletingProject ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : null}
              {t("projects.deleteConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PageHeader({ title }: { title: string }) {
  return (
    <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
      <div className="flex items-center gap-2 px-4">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mr-2 data-[orientation=vertical]:h-4"
        />
        <h1 className="truncate text-lg font-semibold">{title}</h1>
      </div>
    </header>
  );
}

// Detail Page Component
function ChatDetailPage() {
  const { t } = useTranslation();
  const { chatId } = useParams({ from: "/chats/$chatId" });
  const user = useAuthStore((s) => s.user);
  const models = useModelsStore((s) => s.models);
  const messagesByChat = useMessagesStore((s) => s.messagesByChat);
  const loadMessages = useMessagesStore((s) => s.loadMessages);
  const addMessage = useMessagesStore((s) => s.addMessage);
  const chats = useChatsStore((s) => s.chats);
  const updateChat = useChatsStore((s) => s.updateChat);
  const projects = useProjectsStore((s) => s.projects);
  const updateProjectChat = useProjectsStore((s) => s.updateProjectChat);
  const setSending = useChatsStore((s) => s.setSending);
  const isSending = useChatsStore((s) => s.sendingChatIds.has(chatId));
  const chatsLoaded = useChatsStore((s) => s.isLoaded);
  const [isUpdatingProject, setIsUpdatingProject] = useState(false);
  const [isCreateProjectDialogOpen, setIsCreateProjectDialogOpen] =
    useState(false);
  const [downloadingWordMessageId, setDownloadingWordMessageId] = useState<
    number | null
  >(null);

  const canWrite = user?.canWrite ?? false;
  const canGiveFeedback =
    canWrite &&
    (user?.role === "REVIEWER" ||
      user?.role === "MODERATOR" ||
      user?.role === "ADMIN");

  const chatData = messagesByChat[chatId];
  const messages = useMemo(
    () => chatData?.messages ?? [],
    [chatData?.messages]
  );
  const isLoadingMessages = chatData?.isLoading ?? false;

  const currentChat = chats.find((c) => c.id === chatId);
  const currentProject = projects.find(
    (project) => project.id === currentChat?.projectId
  );

  // the server owns this state, so it survives a reload and covers other tabs
  const isPending = currentChat?.pendingSince != null;
  // block until the chat list is there, otherwise a send could race an unknown state
  const isBusy = isSending || isPending || !chatsLoaded;

  const fileData = useChatsStore((s) => s.filesByChat[chatId]);
  const chatFiles = useMemo(() => fileData?.files ?? [], [fileData?.files]);
  const loadFiles = useChatsStore((s) => s.loadFiles);

  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadMessages(chatId);
    loadFiles(chatId);
  }, [chatId, loadMessages, loadFiles]);

  // poll only when no local request is running (e.g. after a reload during processing).
  // watching the chat alone keeps this cheap; the messages are fetched once it is done
  useEffect(() => {
    if (!isPending || isSending) return;

    const interval = setInterval(async () => {
      try {
        const chat = await api.get<ReadChatDto>(`/chats/${chatId}`);
        updateChat(chat);
        if (!chat.pendingSince) await loadMessages(chatId, true);
      } catch {
        // transient failure, the next tick retries
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [isPending, isSending, chatId, loadMessages, updateChat]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const root = scrollAreaRef.current;
    if (!root) return;

    requestAnimationFrame(() => {
      const viewport = root.querySelector<HTMLElement>(
        '[data-slot="scroll-area-viewport"]'
      );
      viewport?.scrollTo({ top: viewport.scrollHeight, behavior });
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const changeChatProject = async (projectId: string | null) => {
    if (!currentChat) return;

    try {
      setIsUpdatingProject(true);
      const updatedChat = await api.patch<ReadChatDto>(
        `/chats/${currentChat.id}/project`,
        { projectId }
      );
      updateChat(updatedChat);
      updateProjectChat(updatedChat);
      toast.success(
        projectId ? t("projects.chatAdded") : t("projects.chatRemoved")
      );
    } catch (error) {
      console.error("Failed to update chat project:", error);
      toast.error(t("projects.chatUpdateError"));
    } finally {
      setIsUpdatingProject(false);
    }
  };

  const handleSend = async (
    content: string,
    files: File[],
    modelId: string
  ) => {
    setSending(chatId, true);

    const tempFiles = files.map((file, index) => ({
      id: -(index + 1),
      chatId,
      messageId: 0,
      openaiFileId: "",
      filename: file.name,
      mimeType: file.type,
      createdAt: new Date(),
      updatedAt: new Date()
    }));

    const tempUserMessage: ReadMessageWithSourcesDto = {
      id: Date.now(),
      chatId,
      modelId: null,
      promptId: null,
      type: "USER",
      content,
      totalTokens: null,
      sources: [],
      files: tempFiles,
      prompt: null,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    addMessage(tempUserMessage);

    try {
      let response: CreateMessageResponseDto;

      if (files.length > 0) {
        const formData = new FormData();
        formData.append("modelId", modelId);
        formData.append("content", content);
        files.forEach((file) => formData.append("files", file));

        response = await api.postFormData<CreateMessageResponseDto>(
          `/chats/${chatId}/messages`,
          formData
        );
      } else {
        response = await api.post<CreateMessageResponseDto>(
          `/chats/${chatId}/messages`,
          {
            modelId: Number(modelId),
            content
          }
        );
      }

      addResponseMessages(response, addMessage);

      if (files.length > 0) {
        api
          .get<ReadChatWithFilesDto>(`/chats/${chatId}`)
          .then((chat) =>
            useChatsStore.getState().setFiles(chatId, chat.files || [])
          )
          .catch(() => {});
      }

      if (response.chat) {
        updateChat(response.chat);
        updateProjectChat(response.chat);
      }
    } catch (error) {
      discardTempMessage(chatId, tempUserMessage.id);

      if (error instanceof ApiException && error.status === 409) {
        toast.error(t("chat.errorAlreadyProcessing"));
      } else if (error instanceof ApiException) {
        toast.error(t("chat.errorSending"), {
          description: error.message
        });
      } else {
        toast.error(t("chat.errorSending"));
      }
    } finally {
      setSending(chatId, false);
    }
  };

  const handleDownloadWord = async (
    message: ReadMessageWithSourcesDto,
    includeSrReferences: boolean
  ) => {
    setDownloadingWordMessageId(message.id);
    try {
      const download = await api.download(
        `/word-export/chats/${chatId}/messages/${message.id}?includeSrReferences=${includeSrReferences}`
      );
      const url = URL.createObjectURL(download.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = download.filename ?? `agent-answer-${message.id}.docx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(t("chat.wordDownloadFailed"), {
        description: error instanceof Error ? error.message : undefined
      });
    } finally {
      setDownloadingWordMessageId(null);
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      {/* Chat Title Header */}
      <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-12">
        <div className="flex items-center gap-2 px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator
            orientation="vertical"
            className="mr-2 data-[orientation=vertical]:h-4"
          />
          <h1 className="text-lg font-semibold truncate">
            {currentChat?.title || t("chats.noTitle")}
          </h1>
        </div>
      </header>

      {/* Messages Area */}
      {isLoadingMessages ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">
            {t("chat.loadingMessages")}
          </span>
        </div>
      ) : messages.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <MessageSquare className="size-12 mx-auto text-muted-foreground/50" />
            <p className="mt-4 text-muted-foreground">{t("chat.noMessages")}</p>
          </div>
        </div>
      ) : (
        <ScrollArea className="flex-1 min-h-0" ref={scrollAreaRef}>
          <div className="space-y-4 max-w-3xl mx-auto px-4 py-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-3",
                  message.type === "USER" ? "justify-end" : "justify-start"
                )}
              >
                {message.type !== "USER" && (
                  <div className="flex-shrink-0 size-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="size-4 text-primary-text" />
                  </div>
                )}
                <div
                  className={cn(
                    "rounded-lg px-4 py-2 max-w-[80%]",
                    message.type === "USER"
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted"
                  )}
                >
                  {message.type === "USER" ? (
                    <>
                      <p className="text-sm whitespace-pre-wrap">
                        {message.content}
                      </p>
                      {message.files && message.files.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {message.files.map((file) => (
                            <span
                              key={file.id}
                              className="inline-flex items-center gap-1 text-xs bg-primary-foreground/10 rounded px-1.5 py-0.5"
                            >
                              <Paperclip className="size-3" />
                              {file.filename}
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <Markdown content={message.content} />
                    </>
                  )}
                  {message.type === "AGENT" &&
                    "sources" in message &&
                    message.sources &&
                    message.sources.length > 0 && (
                      <MessageSources sources={message.sources} />
                    )}
                  <div
                    className={cn(
                      "flex justify-between items-center text-xs mt-1.5 gap-4",
                      message.type === "USER"
                        ? "text-primary-foreground/70"
                        : "text-muted-foreground"
                    )}
                  >
                    <span className="flex items-center flex-wrap gap-1.5">
                      <span>
                        {new Date(message.createdAt).toLocaleTimeString()}
                      </span>
                      {(user?.role === "MODERATOR" ||
                        user?.role === "ADMIN") && (
                        <>
                          {message.modelId && (
                            <span className="rounded bg-foreground/5 px-1.5">
                              {
                                models.find((m) => m.id === message.modelId)
                                  ?.displayName
                              }
                            </span>
                          )}
                          {message.totalTokens && (
                            <span className="rounded bg-foreground/5 px-1.5">
                              {message.totalTokens.toLocaleString()}{" "}
                              {t("chat.tokens")}
                            </span>
                          )}
                          {message.prompt && (
                            <span className="rounded bg-foreground/5 px-1.5">
                              {message.prompt.name}
                            </span>
                          )}
                        </>
                      )}
                    </span>
                    {message.type === "AGENT" && (
                      <span className="flex items-center gap-0.5">
                        <Separator
                          orientation="vertical"
                          className="mr-1.5 data-[orientation=vertical]:h-4"
                        />
                        {canGiveFeedback && (
                          <MessageFeedback
                            chatId={chatId}
                            messageId={message.id}
                            feedback={message.feedback}
                          />
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-6 text-muted-foreground hover:text-foreground"
                              title={t("chat.downloadWord")}
                              disabled={downloadingWordMessageId === message.id}
                            >
                              {downloadingWordMessageId === message.id ? (
                                <Loader2 className="size-3 animate-spin" />
                              ) : (
                                <Download className="size-3" />
                              )}
                              <span className="sr-only">
                                {t("chat.downloadWord")}
                              </span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleDownloadWord(message, true)}
                            >
                              {t("chat.downloadWordWithSr")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDownloadWord(message, false)}
                            >
                              {t("chat.downloadWordWithoutSr")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </span>
                    )}
                  </div>
                </div>
                {message.type === "USER" && (
                  <div className="flex-shrink-0 size-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="size-4 text-primary-foreground" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      )}

      {/* Input Area */}
      <div className="shrink-0 p-4 pt-0">
        {canWrite ? (
          <ChatInput
            onSend={handleSend}
            isSending={isBusy}
            chatFiles={chatFiles}
            projectAssignment={
              currentChat
                ? {
                    projects,
                    currentProjectId: currentChat.projectId,
                    currentProjectName: currentProject?.name,
                    isUpdating: isUpdatingProject,
                    onChange: changeChatProject,
                    onCreateProject: () => setIsCreateProjectDialogOpen(true)
                  }
                : undefined
            }
          />
        ) : (
          <ArchiveNotice />
        )}
      </div>

      <CreateProjectDialog
        open={isCreateProjectDialogOpen}
        onOpenChange={setIsCreateProjectDialogOpen}
        navigateOnCreated={false}
        onCreated={async (project) => {
          await changeChatProject(project.id);
        }}
      />
    </div>
  );
}
